import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from 'react';

const FontSizeContext = createContext(null);

const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 20;
const DEFAULT_FONT_SIZE = 16;
const STEP = 2;

export const FontSizeProvider = ({ children }) => {
    const [baseFontSize, setBaseFontSize] = useState(DEFAULT_FONT_SIZE);

    useEffect(() => {
        // This sets the font size on the root <html> element.
        // CSS using `rem` units will scale accordingly.
        document.documentElement.style.fontSize = `${baseFontSize}px`;
    }, [baseFontSize]);

    const increaseFontSize = useCallback(() => {
        setBaseFontSize(size => Math.min(size + STEP, MAX_FONT_SIZE));
    }, []);

    const decreaseFontSize = useCallback(() => {
        setBaseFontSize(size => Math.max(size - STEP, MIN_FONT_SIZE));
    }, []);

    const resetFontSize = useCallback(() => {
        setBaseFontSize(DEFAULT_FONT_SIZE);
    }, []);

    const value = useMemo(() => ({
        increaseFontSize,
        decreaseFontSize,
        resetFontSize,
    }), [increaseFontSize, decreaseFontSize, resetFontSize]);

    return (
        <FontSizeContext.Provider value={value}>
            {children}
        </FontSizeContext.Provider>
    );
};

export const useFontSize = () => {
    const context = useContext(FontSizeContext);
    if (!context) {
        throw new Error('useFontSize must be used within a FontSizeProvider');
    }
    return context;
};